import { useCallback, useRef, useState } from 'react';
import { describeFailure } from '@/utils/fulfillmentErrors';

/** The tone decides the colour and the icon. */
export type FulfillmentToastTone = 'success' | 'error' | 'warning';

/**
 * Whether a card leaves on its own a few seconds after it can be read (`auto`) or stays until the operator
 * closes it (`manual`). A card the desk can miss without harm leaves; one that says something did not happen,
 * or may not have, stays: an operator who looked away must still find it (SSD-7705).
 */
export type FulfillmentToastDismissal = 'auto' | 'manual';

export interface FulfillmentToastState {
  tone: FulfillmentToastTone;
  text: string;
  /** One line per item, for a batch whose outcome no single sentence can carry. */
  details?: string[];
  /** A worded button for a card the desk answers rather than reads: it does its work, then closes the card. */
  action?: FulfillmentToastAction;
}

export interface FulfillmentToastAction {
  label: string;
  run: () => void;
}

export interface FulfillmentToastOptions {
  action?: FulfillmentToastAction;
  /** Without it a success leaves on its own and an error or a warning stays (`TONE_DISMISSAL`). */
  dismissal?: FulfillmentToastDismissal;
}

/**
 * For a card about the click or entry just made, when nothing was sent and the operator fixes it where they
 * stand: no selection, a value in the wrong form, nothing to export, a step to take first (SSD-7705).
 */
export const AUTO_DISMISS: FulfillmentToastOptions = { dismissal: 'auto' };

/**
 * A new refusal stays unless its caller says otherwise, so a card nobody classified can never leave unread.
 * A success leaves: the screen already shows what it did.
 */
const TONE_DISMISSAL: Record<FulfillmentToastTone, FulfillmentToastDismissal> = {
  success: 'auto',
  error: 'manual',
  warning: 'manual',
};

/** One standing notification; the id is what its timer and its dismiss name it by. */
export interface FulfillmentToastItem extends FulfillmentToastState {
  id: number;
  dismissal: FulfillmentToastDismissal;
}

export interface FulfillmentToastControls {
  /** Every outcome nobody has dismissed yet, oldest first. */
  toasts: FulfillmentToastItem[];
  /** Each `show*` answers the id of the notification it raised, for a caller that ties state to it. */
  showSuccess: (text: string, details?: string[], options?: FulfillmentToastOptions) => number;
  showError: (text: string, details?: string[], options?: FulfillmentToastOptions) => number;
  showWarning: (text: string, details?: string[], options?: FulfillmentToastOptions) => number;
  /** The server's own words when it gave any — an operator cannot act on "the action failed". */
  showFailure: (error: unknown, fallback: string, details?: string[], options?: FulfillmentToastOptions) => number;
  dismiss: (id: number) => void;
}

/**
 * One feedback stack per screen. An outcome is added to what is already standing, never written over
 * it: a refusal that the next success replaced was a refusal nobody read. Two outcomes with the same
 * words stay two notifications, because they answer two actions.
 */
export const useFulfillmentToast = (): FulfillmentToastControls => {
  const [toasts, setToasts] = useState<FulfillmentToastItem[]>([]);
  const lastId = useRef(0);

  const show = useCallback(
    (tone: FulfillmentToastTone, text: string, details?: string[], options?: FulfillmentToastOptions) => {
      lastId.current += 1;
      const id = lastId.current;
      setToasts((standing) => [
        ...standing,
        {
          id,
          tone,
          text,
          dismissal: options?.dismissal ?? TONE_DISMISSAL[tone],
          ...(details && details.length > 0 ? { details } : {}),
          ...(options?.action ? { action: options.action } : {}),
        },
      ]);
      return id;
    },
    [],
  );

  const showSuccess = useCallback(
    (text: string, details?: string[], options?: FulfillmentToastOptions) => show('success', text, details, options),
    [show],
  );
  const showError = useCallback(
    (text: string, details?: string[], options?: FulfillmentToastOptions) => show('error', text, details, options),
    [show],
  );
  const showWarning = useCallback(
    (text: string, details?: string[], options?: FulfillmentToastOptions) => show('warning', text, details, options),
    [show],
  );
  const showFailure = useCallback(
    (error: unknown, fallback: string, details?: string[], options?: FulfillmentToastOptions) =>
      showError(describeFailure(error, fallback), details, options),
    [showError],
  );
  const dismiss = useCallback((id: number) => setToasts((standing) => standing.filter((toast) => toast.id !== id)), []);

  return { toasts, showSuccess, showError, showWarning, showFailure, dismiss };
};
